﻿import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Link,
} from "@mui/material";
import { CheckIcon, ErrorBox, ResultInfo } from "@/ImageRepo";
import { Close as CloseIcon } from "@mui/icons-material";
import "./WorkflowStepsPopup.css";
import { useDispatch, useSelector } from "react-redux";
import {
  selectWorkflowSteps,
  selectSelectedRecord,
  selectWorkflowStepsAreLoading,
  fetchWorkflowSteps,
  fetchMissingCrossReferenceData,
} from "@/features/homepage/workflowManagerSlice";
import {
  setIsMapOnFlyOpen,
  selectMapOnFlyInfoLoading,
  clearMapOnFlyInfo,
} from "@/features/connections/crossReferenceSlice";
import { useTranslation } from "react-i18next";
import { StandardFeatureType, WithTranslation } from "@/utils/utils";
import {
  setIsWorkflowSettingsOpen,
  fetchSingleConnectionPair,
  setSelectedConnectionPairs,
} from "@/features/settings/settingsSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import { selectWorkflow } from "@/features/homepage/workflowManagerSessionSlice";
import LoadingBar from "@/components/shared/LoadingBar";
const ProcessingStatus = {
  EnumNotFound: -1,
  InQueue: 0,
  Processed: 10,
  Error: 11,
};
const StatusDot = ({ statusName }) => {
  return <div className={"statusDot " + statusName} />;
};
const StatusCircle = ({ statusName }) => {
  return <div className={"stepIcon statusCircle " + statusName} />;
};
const ChangeSettingsText = ({
  lastStatus,
  onClose,
  sourceConnectionId,
  targetConnectionId,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("homepage");

  const openSettings = () => {
    dispatch(
      fetchSingleConnectionPair({ sourceConnectionId, targetConnectionId })
    )
      .then(unwrapResult)
      .then((response) => {
        dispatch(
          setSelectedConnectionPairs({
            selectedConnectionPairs: [response],
          })
        );
        dispatch(setIsWorkflowSettingsOpen({ isOpen: true }));
        onClose();
      });
  };

  return (
    <>
      <div className="stepHeader">
        <div className="header-step-icon">
          {lastStatus === ProcessingStatus.Processed ? (
            <StatusCircle statusName="Processed" />
          ) : lastStatus === ProcessingStatus.InQueue ? (
            <StatusCircle statusName="InQueue" />
          ) : lastStatus === ProcessingStatus.Error ? (
            <StatusCircle statusName="Error" />
          ) : (
            <StatusCircle statusName="Disabled" />
          )}
        </div>
        <Typography variant="body" className="settings-text">
          {t(
            "workflow-steps.summary.navigateToSetting-text",
            "to change settings please click the link"
          )}
        </Typography>
        <Typography
          variant="body"
          className="settings-link"
          onClick={openSettings}
        >
          {t(
            "workflow-steps.summary.navigateToSettingLink",
            "Workflow Settings"
          )}
        </Typography>
      </div>
    </>
  );
};

const RenderListItems = ({ substeps, t, mapOnFlyIsReady, dispatch }) => {
  var afterError = false;

  const triggerMapOnFly = () => {
      dispatch(setIsMapOnFlyOpen({ value: true }));

      // heap.io
      window.heap.track("Map on Fly - Open Screen");
  };
  const ValidateIfMapOnFly = (substep) => {
    if (
      mapOnFlyIsReady &&
      substep?.Status === ProcessingStatus.Error &&
      substep?.Type === StandardFeatureType.CrossReference
    ) {
      return true;
    }
    return false;
  };

  return substeps?.map((substep, index) => {
    var statusName = "Disabled";
    Object.keys(ProcessingStatus)?.map(function (key) {
      if (substep?.Status === ProcessingStatus[key] && !afterError) {
        statusName = key;
      }
    });
    if (substep?.Status === ProcessingStatus.Error) {
      afterError = true;
    }
    return (
      <ListItem key={index} className="listItem">
        <StatusDot statusName={statusName} />
        <ListItemText
          className="substepTextSection"
          primary={
            <Typography
              variant="body"
              className={
                "substepText " +
                (substep?.Status === ProcessingStatus.Error
                  ? "error-text"
                  : afterError === true ||
                    substep?.Status === ProcessingStatus.EnumNotFound
                  ? "disabled-text"
                  : "")
              }
            >
              {ValidateIfMapOnFly(substep) ? (
                <>
                  <Link onClick={triggerMapOnFly} className="substep-link">
                    {WithTranslation(t, substep)}
                  </Link>
                </>
              ) : (
                <>{WithTranslation(t, substep)}</>
              )}
            </Typography>
          }
        />
        <ListItemIcon className="listItemIcon">
          {substep?.Status === ProcessingStatus.Processed ? (
            <CheckIcon className="check" />
          ) : substep?.Status === ProcessingStatus.InQueue ||
            substep?.Status === ProcessingStatus.EnumNotFound ? (
            <></>
          ) : (
            /*else error*/
            <img src={ErrorBox} className="errorBox" />
          )}
        </ListItemIcon>
      </ListItem>
    );
  });
};

const RenderSteps = (steps, mapOnFlyIsReady, dispatch) => {
  const { t } = useTranslation("homepage");

  if (!steps) {
    return null;
  }
  return steps?.map((step, index) => (
    <>
      <div key={index} className="workflow-step-section">
        <div className="stepHeader">
          <div className="header-step-icon">
            {step?.Status === ProcessingStatus.Processed ? (
              <StatusCircle statusName="Processed" />
            ) : step?.Status === ProcessingStatus.InQueue ? (
              <StatusCircle statusName="InQueue" />
            ) : step?.Status === ProcessingStatus.Error ? (
              <StatusCircle statusName="Error" />
            ) : (
              <StatusCircle statusName="Disabled" />
            )}
          </div>
          <Typography
            variant="body"
            className={
              step?.Status === ProcessingStatus.EnumNotFound
                ? "disabled-text"
                : ""
            }
          >
            {WithTranslation(t, step)}
          </Typography>
        </div>
        <List>
          {RenderListItems({
            substeps: step?.AdditionalSteps,
            t: t,
            mapOnFlyIsReady,
            dispatch,
          })}
        </List>
      </div>
    </>
  ));
};

const RenderSummary = (summarySection) => {
  const { t } = useTranslation("homepage");

  if (!summarySection) {
      return null;
  }

  return summarySection?.map((line, index) => (
    <>
      <Typography variant="body" index={index} display="block">
        {WithTranslation(t, line)}
      </Typography>
    </>
  ));
};

const WorkflowStepsPopup = ({ open, onClose }) => {
  const { t } = useTranslation("homepage");

  const dispatch = useDispatch();
  const currentOperationId = useSelector(selectWorkflow);
  const isLoading = useSelector(selectWorkflowStepsAreLoading);
  const {
    WorkflowSteps: steps,
    SummarySection,
    SourceConnectionId,
    TargetConnectionId,
    WorkflowSchedulingDisabled,
  } = useSelector(selectWorkflowSteps);
  const selectedRecord = useSelector(selectSelectedRecord);
  const missingCrossReferencesLoading = useSelector(selectMapOnFlyInfoLoading);

  useEffect(() => {
    if (selectedRecord) {
      dispatch(
        fetchWorkflowSteps({
          operationId: currentOperationId,
          row: selectedRecord,
        })
      );
    }
  }, [selectedRecord]);

  useEffect(() => {
    if (open && validateIfErrorDueToUnmappedCrossRefs(steps)) {
      dispatch(fetchMissingCrossReferenceData({ currentOperationId }));
    }
    if (!open) {
      dispatch(clearMapOnFlyInfo());
    }
  }, [open, steps]);

  const validateIfErrorDueToUnmappedCrossRefs = (steps) => {
    return steps?.some(
      (x) =>
        x?.Status === ProcessingStatus.Error &&
        x?.AdditionalSteps?.some(
          (y) => y?.Type === StandardFeatureType.CrossReference
        )
    );
  };

  const summarySection = RenderSummary(SummarySection);
  const stepsList = RenderSteps(
    steps,
    !missingCrossReferencesLoading,
    dispatch
  );

  const changeSettingsText = ChangeSettingsText({
    lastStatus: steps?.at(-1)?.Status,
    onClose,
    sourceConnectionId: SourceConnectionId,
    targetConnectionId: TargetConnectionId,
  });
  return (
    <>
      <Dialog
        className="workflow-steps-popup"
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: "dialogPaper",
          sx: {
            zIndex: 1302,
            marginLeft: "auto",
            transform: "translateX(0)",
          },
        }}
        BackdropProps={{ sx: { zIndex: 1301, position: "absolute" } }}
      >
        <DialogTitle disableTypography className="header">
          <Typography variant="subtitle">Workflow Steps</Typography>
          <IconButton className="closeIconButton" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="dialogContent">
          {isLoading && <LoadingBar isDataLoaded={!isLoading} />}
          {!isLoading && (
            <>
              {WorkflowSchedulingDisabled && (
                <Paper className="automation-disabled-paper">
                  <ResultInfo className="automation-disabled-icon" />
                  <span className="automation-disabled-message">
                    {t(
                      "workflow-steps.summary.automationSchedulerDisabled",
                      "Automation is not currently scheduled for this connection pair."
                    )}
                  </span>
                </Paper>
              )}

              <Paper className="status">{summarySection}</Paper>
              <div className="stepBody">
                <div className="side-bar">
                  <div className="clear-line" />
                  <div className="grey-line" />
                  <div className="clear-line clear-line-bottom" />
                </div>
                <div className="stepContainer">
                  {
                    // SHEP-193: Passing dispatch as a param to child function, if we declare it in the child function, React complains about React Hook order not being followed.
                    // Ref: https://react.dev/reference/rules/rules-of-hooks
                    stepsList
                  }
                  {changeSettingsText}
                </div>
              </div>
            </>
          )}
        </DialogContent>
        <DialogContent className="footer">
          <DialogActions>
            <Button
              className="closeButton"
              onClick={onClose}
              color="primary"
              variant="contained"
            >
              {t("workflow-steps.summary.close-text", "Close")}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WorkflowStepsPopup;
